import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Manager, Reference, Popper } from 'react-popper';
import noop from 'lodash/noop';
import { useSparkConfig } from 'components/SparkConfigProvider';

import style from './Tooltip.module.scss';

function isVisiblyFocused(element) {
  try {
    return element.matches(':focus-visible');
  } catch (e) {
    // Handle browsers that cannot match on `':focus-visible'`, though all supported browsers ought...
    return element.contains(document.activeElement);
  }
}

/**
 * Tooltip can wrap most elements and provide tooltips on hover (by default) or be controlled. It uses `react-popper` internally.
 */
export default function Tooltip({
  children,
  tooltipContent,
  classes,
  className,
  placement,
  isOpen,
  popperProps,
  portalNode,
  active,
  onClickAway,
  onOutOfBoundaries,
}) {
  const [isShown, setIsShown] = useState(false);
  const { getPopoverContainer } = useSparkConfig();
  const listenerProps = {
    captures: true,
    passive: true,
  };
  const popperRef = React.createRef();

  useEffect(() => {
    const onWindowClick = e => {
      const el = popperRef.current;
      if (!el?.contains(e.target)) {
        onClickAway();
      }
    };
    if (onClickAway !== noop) {
      window.addEventListener('click', onWindowClick, listenerProps);
    }
    return () => {
      window.removeEventListener('click', onWindowClick, listenerProps);
    };
  }, [onClickAway]);

  const showTooltipWhenActive = () => {
    if (active) {
      setIsShown(true);
    }
  };

  const onFocus = e => {
    /**
     * Match rest of our relevant styling/focus-content by specifically dealing with "focus-visible"
     * For example, this appears when clicking in or tabbing in, but not when mousing out and then
     * changing browser tabs away and back.
     */
    if (isVisiblyFocused(e.target)) {
      showTooltipWhenActive();
    }
  };

  const hideTooltip = () => {
    if (isShown) {
      setIsShown(false);
    }
  };

  return (
    <Manager tag={false}>
      <Reference>
        {({ ref }) => (
          <div
            className={classnames(className)}
            ref={ref}
            onMouseEnter={showTooltipWhenActive}
            onFocus={onFocus}
            onMouseLeave={hideTooltip}
            onBlur={hideTooltip}
          >
            {children}
          </div>
        )}
      </Reference>
      {(isShown || isOpen) &&
        ReactDOM.createPortal(
          <Popper placement={placement} {...popperProps}>
            {({
              ref,
              outOfBoundaries,
              style: popperStyle,
              placement: popperPlacement,
              arrowProps,
            }) => {
              if (outOfBoundaries && onOutOfBoundaries) {
                onOutOfBoundaries({ isShown, isOpen, hideTooltip });
              }
              return (
                <div
                  ref={ref}
                  style={popperStyle}
                  data-placement={popperPlacement}
                  data-out-of-boundaries={outOfBoundaries}
                  className={classnames(style.wrap, classes.tooltipWrap)}
                >
                  <div
                    ref={popperRef}
                    className={classnames(style.tooltip, classes.tooltip)}
                  >
                    {tooltipContent}
                  </div>
                  <div
                    ref={arrowProps.ref}
                    style={arrowProps.style}
                    className={classnames(style.arrowWrap, classes.arrowWrap)}
                    data-placement={popperPlacement}
                  >
                    <div
                      className={classnames(
                        style.arrowBorder,
                        classes.arrowBorder,
                      )}
                    />
                    <div className={classnames(style.arrow, classes.arrow)} />
                  </div>
                </div>
              );
            }}
          </Popper>,
          portalNode || getPopoverContainer(null),
        )}
    </Manager>
  );
}

Tooltip.displayName = 'Tooltip';

Tooltip.propTypes = {
  /**
   * content that initiates the tooltip
   */
  children: PropTypes.node.isRequired,
  /**
   * content of the tooltip
   */
  tooltipContent: PropTypes.node,
  classes: PropTypes.shape({
    arrow: PropTypes.string,
    arrowBorder: PropTypes.string,
    arrowWrap: PropTypes.string,
    tooltip: PropTypes.string,
    tooltipWrap: PropTypes.string,
  }),
  /**
   * additional classes for the wrapper
   */
  className: PropTypes.string,
  /**
   * Popper `placement` option
   */
  placement: PropTypes.string,
  /**
   * Any other popper props that are spread onto <Popper />
   */
  popperProps: PropTypes.shape({}),
  /**
   * parent of popper
   */
  portalNode: PropTypes.shape({}),
  /**
   * Keep tooltip opened by default
   */
  isOpen: PropTypes.bool,
  /**
   * condition to respond to hover events
   */
  active: PropTypes.bool,
  /**
   * Event to run on click away
   */
  onClickAway: PropTypes.func,
  /**
   * Event to run when the reference element moves out of bounds
   */
  onOutOfBoundaries: PropTypes.func,
};

Tooltip.defaultProps = {
  tooltipContent: '',
  classes: {},
  className: '',
  placement: 'bottom',
  isOpen: false,
  active: true,
  popperProps: {},
  portalNode: null,
  onClickAway: noop,
  onOutOfBoundaries: noop,
};
